import React from 'react';

// Utility to normalize 3-digit hex to 6-digit
function normalizeHexColor(hex) {
  if (/^#[0-9a-fA-F]{3}$/.test(hex)) {
    return (
      '#' + hex[1] + hex[1] + hex[2] + hex[2] + hex[3] + hex[3]
    );
  }
  return hex;
}

const BlockRenderer = ({
  blocks = [],
  quantity = 1,
  selectedWeight,
  selectedBeanType,
  onAddToCart,
  onQuantityChange,
  onWeightChange,
  onBeanTypeChange,
  featuredProduct,
  handleAddToCartWithGrams,
  activeTab,
}) => {
  // DEBUG: Log selectedBeanType whenever it changes
  React.useEffect(() => {
    // Remove all debug logs
  }, [selectedBeanType]);

  return (
    <>
      <div
        className="tab-content-animate"
        key={activeTab || (blocks[0]?.id || Math.random())}
      >
      {/* Show selected bean type label if typeSelector and selectedBeanType exist */}
      {(() => {
        const typeSelector = blocks.find(
          b => b.type === 'button' && b.buttonType === 'typeSelector'
        );
        if (typeSelector && selectedBeanType) {
          const selectedTypeOption = typeSelector.options?.find(
            opt => String(opt.type) === String(selectedBeanType) || String(opt.label).toLowerCase() === String(selectedBeanType).toLowerCase()
          );
          const typeLabel = selectedTypeOption?.label || selectedBeanType;
          return (
            <div
              style={{
                position: 'absolute',
                top: 8,
                left: 8,
                background: '#eee',
                color: '#222',
                fontWeight: 700,
                fontSize: 16,
                padding: '4px 12px',
                borderRadius: 8,
                zIndex: 20,
              }}
            >
              {typeLabel}
            </div>
          );
        }
        return null;
      })()}
      {blocks.filter(Boolean).map(block => {
        if (!block || !block.type) return null;
        if (block.type === 'text') {
          let displayContent = block.content;
          if (block.isDynamicPrice && selectedWeight) {
            // Find the weightSelector block in the same tab
            const weightSelector = blocks.find(b => b.type === 'button' && b.buttonType === 'weightSelector');
            const selectedOption = weightSelector?.options?.find(opt => String(opt.weight) === String(selectedWeight));
            const price = selectedOption?.price;
            displayContent = (price !== undefined && price !== null) ? `${price} QAR` : '';
          }
          return (
            <div
              key={block.id}
              style={{
                position: 'absolute',
                left: block.x,
                top: block.y,
                width: block.width,
                height: block.height,
                fontSize: block.fontSize,
                fontWeight: block.fontWeight,
                color: normalizeHexColor(block.color || '#111'),
                fontFamily: block.fontFamily,
                letterSpacing: block.letterSpacing,
                lineHeight: block.lineHeight,
                textAlign: (typeof block.textAlign === 'string' && block.textAlign.length > 0) ? block.textAlign : 'left',
                textShadow: block.textShadow,
                outline: block.outline,
                background: 'transparent',
                padding: 4,
                borderRadius: 4,
                minWidth: 40,
                whiteSpace: block.wrapText === false ? 'nowrap' : 'pre-line',
                overflow: 'hidden',
                transform: `rotate(${Number(block.rotation) || 0}deg)`,
                boxShadow: block.boxShadow,
                zIndex: 2,
                pointerEvents: 'none',
              }}
            >
              {displayContent}
            </div>
          );
        }
        if (block.type === 'image') {
          return (
            <img
              key={block.id}
              src={block.src}
              alt=""
              style={{
                position: 'absolute',
                left: block.x,
                top: block.y,
                width: block.width,
                height: block.height,
                objectFit: 'contain',
                transform: `rotate(${Number(block.rotation) || 0}deg)`,
                pointerEvents: 'none',
                opacity: block.opacity ?? 1,
              }}
            />
          );
        }
        if (block.type === 'button') {
          if (block.buttonType === 'addToCart') {
            return (
              <button
                key={block.id}
                className="add-to-cart"
                style={{
                  position: 'absolute',
                  left: block.x,
                  top: block.y,
                  width: block.width,
                  height: block.height,
                  fontSize: block.fontSize,
                  fontWeight: block.fontWeight,
                  color: normalizeHexColor(block.color || '#fff'),
                  border: 'none',
                  transform: `rotate(${Number(block.rotation) || 0}deg)`,
                  pointerEvents: 'auto',
                  zIndex: 10,
                }}
                onClick={() => (handleAddToCartWithGrams ? handleAddToCartWithGrams(featuredProduct, quantity) : onAddToCart && onAddToCart(quantity))}
              >
                <svg style={{marginRight: 10, marginBottom: -2}} width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="8" cy="19" r="1.2" stroke="#fff" strokeWidth="1.5" /><circle cx="17" cy="19" r="1.2" stroke="#fff" strokeWidth="1.5" /><path d="M2 2H4.5L6.5 14.5H18L20 6.5H6.5" stroke="#fff" strokeWidth="1.5" strokeLinejoin="round" /></svg>
                {block.content}
              </button>
            );
          }
          if (block.buttonType === 'increaseQuantity') {
            return (
              <button
                key={block.id}
                style={{
                  position: 'absolute',
                  left: block.x,
                  top: block.y,
                  width: block.width,
                  height: block.height,
                  fontSize: block.fontSize,
                  fontWeight: block.fontWeight,
                  color: normalizeHexColor(block.color || '#111'),
                  backgroundColor: normalizeHexColor(block.background || '#fff'),
                  borderRadius: block.borderRadius,
                  border: 'none',
                  transform: `rotate(${Number(block.rotation) || 0}deg)`,
                  pointerEvents: 'auto',
                }}
                onClick={() => onQuantityChange && onQuantityChange(q => q + 1)}
              >
                {block.content}
              </button>
            );
          }
          if (block.buttonType === 'decreaseQuantity') {
            return (
              <button
                key={block.id}
                style={{
                  position: 'absolute',
                  left: block.x,
                  top: block.y,
                  width: block.width,
                  height: block.height,
                  fontSize: block.fontSize,
                  fontWeight: block.fontWeight,
                  color: normalizeHexColor(block.color || '#111'),
                  backgroundColor: normalizeHexColor(block.background || '#fff'),
                  borderRadius: block.borderRadius,
                  border: 'none',
                  transform: `rotate(${Number(block.rotation) || 0}deg)`,
                  pointerEvents: 'auto',
                }}
                onClick={() => onQuantityChange && onQuantityChange(q => (q > 1 ? q - 1 : 1))}
              >
                {block.content}
              </button>
            );
          }
          if (block.buttonType === 'quantityDisplay') {
            return (
              <div
                key={block.id}
                style={{
                  position: 'absolute',
                  left: block.x,
                  top: block.y,
                  width: block.width,
                  height: block.height,
                  fontSize: block.fontSize,
                  fontWeight: block.fontWeight,
                  color: normalizeHexColor(block.color || '#111'),
                  backgroundColor: normalizeHexColor(block.background || '#fff'),
                  borderRadius: block.borderRadius,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transform: `rotate(${Number(block.rotation) || 0}deg)`,
                  pointerEvents: 'none',
                }}
              >
                {quantity}
              </div>
            );
          }
          if (block.buttonType === 'gramsDisplay') {
            const gramsPerUnit = 250; // can be made configurable
            return (
              <div
                key={block.id}
                style={{
                  position: 'absolute',
                  left: block.x,
                  top: block.y,
                  width: block.width,
                  height: block.height,
                  fontSize: block.fontSize,
                  fontWeight: block.fontWeight,
                  color: normalizeHexColor(block.color || '#111'),
                  backgroundColor: normalizeHexColor(block.background || '#fff'),
                  borderRadius: block.borderRadius,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transform: `rotate(${Number(block.rotation) || 0}deg)`,
                  pointerEvents: 'none',
                }}
              >
                {quantity * gramsPerUnit}g
              </div>
            );
          }
          if (block.buttonType === 'weightSelector') {
            // Compute RGBA background robustly
            let bg = normalizeHexColor(block.background || '#fff');
            let opacity = typeof block.backgroundOpacity === 'number' ? block.backgroundOpacity : 1;
            function hexToRgba(hex, alpha) {
              let c = hex.replace('#', '');
              if (c.length === 3) c = c.split('').map(x => x + x).join('');
              if (c.length !== 6) return hex; // fallback
              const num = parseInt(c, 16);
              return `rgba(${(num >> 16) & 255}, ${(num >> 8) & 255}, ${num & 255}, ${alpha})`;
            }
            let background;
            if (!!block.noButtonBackground) {
              background = 'transparent';
            } else if (bg.startsWith('#')) {
              background = hexToRgba(bg, opacity);
            } else if (bg.startsWith('rgba')) {
              background = bg;
            } else if (bg.startsWith('rgb')) {
              background = bg.replace('rgb(', 'rgba(').replace(')', `,${opacity})`);
            } else {
              background = bg;
            }
            const buttonRadius = typeof block.buttonRadius === 'number' ? block.buttonRadius : 16;
            return (
              <div
                key={block.id}
                style={{
                  position: 'absolute',
                  left: block.x,
                  top: block.y,
                  width: block.width,
                  height: block.height,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  backgroundColor: normalizeHexColor(background),
                  borderRadius: block.borderRadius,
                  boxShadow: block.boxShadow,
                  outline: block.outline,
                  fontSize: block.fontSize,
                  fontWeight: block.fontWeight,
                  color: normalizeHexColor(block.color || '#111'),
                  zIndex: 2,
                }}
              >
                <span>{block.label || 'Weight'}:</span>
                {Array.isArray(block.options) && block.options.length > 0 && block.options.filter(opt => opt.available).map(opt => {
                  const isActive = String(selectedWeight) === String(opt.weight);
                  return (
                    <button
                      key={opt.weight}
                      onClick={() => onWeightChange && onWeightChange(opt.weight)}
                      style={{
                        padding: '4px 12px',
                        borderRadius: buttonRadius,
                        backgroundColor: isActive ? '#000' : '#eee',
                        color: isActive ? '#fff' : '#222',
                        marginLeft: 4,
                        borderWidth: isActive ? '2px' : '1px',
                        borderStyle: 'solid',
                        borderColor: isActive ? '#000' : '#ccc',
                        fontWeight: 600,
                        cursor: 'pointer',
                        fontSize: block.fontSize,
                        transition: 'all 0.2s',
                        fontStyle: opt.default ? 'italic' : 'normal',
                      }}
                    >
                      {opt.weight}{block.unit}{opt.default ? ' ★' : ''}
                    </button>
                  );
                })}
              </div>
            );
          }
          if (block.buttonType === 'beanTypeSelector') {
            // Compute RGBA background robustly
            let bg = normalizeHexColor(block.background || '#fff');
            let opacity = typeof block.backgroundOpacity === 'number' ? block.backgroundOpacity : 1;
            function hexToRgba(hex, alpha) {
              let c = hex.replace('#', '');
              if (c.length === 3) c = c.split('').map(x => x + x).join('');
              if (c.length !== 6) return hex; // fallback
              const num = parseInt(c, 16);
              return `rgba(${(num >> 16) & 255}, ${(num >> 8) & 255}, ${num & 255}, ${alpha})`;
            }
            let background;
            if (!!block.noButtonBackground) {
              background = 'transparent';
            } else if (bg.startsWith('#')) {
              background = hexToRgba(bg, opacity);
            } else if (bg.startsWith('rgba')) {
              background = bg;
            } else if (bg.startsWith('rgb')) {
              background = bg.replace('rgb(', 'rgba(').replace(')', `,${opacity})`);
            } else {
              background = bg;
            }
            const buttonRadius = typeof block.buttonRadius === 'number' ? block.buttonRadius : 16;
            return (
              <div
                key={block.id}
                style={{
                  position: 'absolute',
                  left: block.x,
                  top: block.y,
                  width: block.width,
                  height: block.height,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  backgroundColor: normalizeHexColor(background),
                  borderRadius: block.borderRadius,
                  boxShadow: block.boxShadow,
                  outline: block.outline,
                  fontSize: block.fontSize,
                  fontWeight: block.fontWeight,
                  color: normalizeHexColor(block.color || '#111'),
                  zIndex: 2,
                }}
              >
                <span>{block.label || 'Type'}:</span>
                {Array.isArray(block.options) && block.options.length > 0 && block.options.map(opt => {
                  const price = featuredProduct?.prices?.[opt]?.[selectedWeight];
                  return (
                    <button
                      key={opt}
                      onClick={() => onBeanTypeChange && onBeanTypeChange(opt)}
                      style={{
                        padding: '4px 12px',
                        borderRadius: buttonRadius,
                        backgroundColor: normalizeHexColor(String(selectedBeanType) === String(opt) ? '#000' : '#eee'),
                        color: normalizeHexColor(String(selectedBeanType) === String(opt) ? '#fff' : '#222'),
                        marginLeft: 4,
                        borderWidth: String(selectedBeanType) === String(opt) ? '2px' : '1px',
                        borderStyle: 'solid',
                        borderColor: String(selectedBeanType) === String(opt) ? '#000' : '#ccc',
                        fontWeight: 600,
                        cursor: 'pointer',
                        fontSize: block.fontSize,
                        transition: 'all 0.2s',
                      }}
                    >
                      {opt.charAt(0).toUpperCase() + opt.slice(1)}{price !== undefined && price !== '' ? ` (${price} QAR)` : ''}
                    </button>
                  );
                })}
              </div>
            );
          }
          if (block.buttonType === 'typeSelector') {
            let bg = normalizeHexColor(block.background || '#fff');
            let opacity = typeof block.backgroundOpacity === 'number' ? block.backgroundOpacity : 1;
            function hexToRgba(hex, alpha) {
              let c = hex.replace('#', '');
              if (c.length === 3) c = c.split('').map(x => x + x).join('');
              if (c.length !== 6) return hex; // fallback
              const num = parseInt(c, 16);
              return `rgba(${(num >> 16) & 255}, ${(num >> 8) & 255}, ${num & 255}, ${alpha})`;
            }
            let background;
            if (!!block.noBackground || !!block.noButtonBackground) {
              background = 'transparent';
            } else if (bg.startsWith('#')) {
              background = hexToRgba(bg, opacity);
            } else if (bg.startsWith('rgba')) {
              background = bg;
            } else if (bg.startsWith('rgb')) {
              background = bg.replace('rgb(', 'rgba(').replace(')', `,${opacity})`);
            } else {
              background = bg;
            }
            const buttonRadius = typeof block.buttonRadius === 'number' ? block.buttonRadius : 16;
            return (
              <div
                key={block.id}
                style={{
                  position: 'absolute',
                  left: block.x,
                  top: block.y,
                  width: block.width,
                  height: block.height,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  backgroundColor: background,
                  borderRadius: block.borderRadius,
                  boxShadow: block.boxShadow,
                  outline: block.outline,
                  fontSize: block.fontSize,
                  fontWeight: block.fontWeight,
                  color: normalizeHexColor(block.color || '#111'),
                  zIndex: 2,
                }}
              >
                <span>{block.label || 'Type'}:</span>
                {Array.isArray(block.options) && block.options.length > 0 && block.options.filter(opt => opt.available).map(opt => {
                  const isActive = String(selectedBeanType) === String(opt.type);
                  return (
                    <button
                      key={opt.type || opt.label}
                      onClick={() => {
                        onBeanTypeChange && onBeanTypeChange(opt.type);
                      }}
                      style={{
                        padding: '4px 12px',
                        borderRadius: buttonRadius,
                        backgroundColor: isActive ? '#000' : '#eee',
                        color: isActive ? '#fff' : '#222',
                        marginLeft: 4,
                        borderWidth: isActive ? '2px' : '1px',
                        borderStyle: 'solid',
                        borderColor: isActive ? '#000' : '#ccc',
                        fontWeight: 700,
                        cursor: 'pointer',
                        fontSize: block.fontSize,
                        transition: 'all 0.2s',
                      }}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            );
          }
          // fallback
          return null;
        }
        if (block.type === 'display') {
          if (block.displayType === 'quantity') {
            return (
              <div
                key={block.id}
                style={{
                  position: 'absolute',
                  left: block.x,
                  top: block.y,
                  width: block.width,
                  height: block.height,
                  fontSize: block.fontSize,
                  fontWeight: block.fontWeight,
                  color: block.color,
                  backgroundColor: block.background,
                  borderRadius: block.borderRadius,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transform: `rotate(${Number(block.rotation) || 0}deg)`,
                  pointerEvents: 'auto',
                  gap: 8,
                }}
              >
                <button onClick={() => onQuantityChange && onQuantityChange(q => (q > 1 ? q - 1 : 1))} style={{ fontSize: block.fontSize, borderRadius: 4, borderWidth: '1px', borderStyle: 'solid', borderColor: '#ccc', backgroundColor: '#fff', width: 28, height: 28, cursor: 'pointer' }}>-</button>
                <span style={{ minWidth: 32, textAlign: 'center' }}>{quantity}</span>
                <button onClick={() => onQuantityChange && onQuantityChange(q => q + 1)} style={{ fontSize: block.fontSize, borderRadius: 4, borderWidth: '1px', borderStyle: 'solid', borderColor: '#ccc', backgroundColor: '#fff', width: 28, height: 28, cursor: 'pointer' }}>+</button>
              </div>
            );
          }
          if (block.displayType === 'grams') {
            return (
              <div
                key={block.id}
                style={{
                  position: 'absolute',
                  left: block.x,
                  top: block.y,
                  width: block.width,
                  height: block.height,
                  fontSize: block.fontSize,
                  fontWeight: block.fontWeight,
                  color: block.color,
                  backgroundColor: block.background,
                  borderRadius: block.borderRadius,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transform: `rotate(${Number(block.rotation) || 0}deg)`,
                  pointerEvents: 'none',
                }}
              >
                {block.label}: {quantity * (block.gramsPerUnit || 250)}{block.unit || 'g'}
              </div>
            );
          }
          if (block.displayType === 'beanType') {
            return (
              <div
                key={block.id}
                style={{
                  position: 'absolute',
                  left: block.x,
                  top: block.y,
                  width: block.width,
                  height: block.height,
                  fontSize: block.fontSize,
                  fontWeight: block.fontWeight,
                  color: block.color,
                  backgroundColor: block.background,
                  borderRadius: block.borderRadius,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transform: `rotate(${Number(block.rotation) || 0}deg)`,
                  pointerEvents: 'none',
                }}
              >
                {block.label}: {selectedBeanType ? selectedBeanType.charAt(0).toUpperCase() + selectedBeanType.slice(1) : ''}
              </div>
            );
          }
          return null;
        }
        if (block.type === 'video') {
          return (
            <video
              key={block.id}
              src={block.src}
              style={{
                position: 'absolute',
                left: block.x,
                top: block.y,
                width: block.width,
                height: block.height,
                objectFit: 'contain',
                borderRadius: block.borderRadius,
                boxShadow: block.boxShadow,
                outline: block.outline,
                opacity: block.opacity ?? 1,
                transform: `rotate(${Number(block.rotation) || 0}deg)`,
                pointerEvents: 'none',
                zIndex: 2,
              }}
              controls
              loop
              autoPlay
              muted
            />
          );
        }
        if (block.type === 'pricing') {
          // Find the weightSelector block in the same tab
          const weightSelector = blocks.find(b => b.type === 'button' && b.buttonType === 'weightSelector');
          const selectedOption = weightSelector?.options?.find(opt => String(opt.weight) === String(selectedWeight));
          const price = selectedOption?.price;
          const total = (price !== undefined && price !== null) ? price * (quantity || 1) : 0;
          return (
            <div
              key={block.id}
              style={{
                position: 'absolute',
                left: block.x,
                top: block.y,
                width: block.width,
                height: block.height,
                fontSize: block.fontSize,
                fontWeight: block.fontWeight,
                color: normalizeHexColor(block.color || '#111'),
                background: block.noBackground ? 'transparent' : (block.background || 'rgba(255,255,255,0.85)'),
                borderRadius: block.borderRadius,
                boxShadow: block.boxShadow,
                outline: block.outline,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: block.textAlign || 'center',
                transform: `rotate(${Number(block.rotation) || 0}deg)`,
                zIndex: 2,
                pointerEvents: 'none',
              }}
            >
              {`${total} QAR`}
            </div>
          );
        }
        return null;
      })}
      </div>
    </>
  );
};

export default BlockRenderer; 